import React, { useState, useEffect, useCallback } from 'react';

interface Domain {
    domain: string;
    active_count: number;
    proven_active_count: number | null;
    unproven_active_count: number | null;
    total_count: number;
    blocked: boolean;
    block_reason: string | null;
    last_updated: string;
}

const DomainsDashboard: React.FC = () => {
    const [domains, setDomains] = useState<Domain[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalDomains, setTotalDomains] = useState(0);
    const [filter, setFilter] = useState<'all' | 'active' | 'blocked'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [actionInProgress, setActionInProgress] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: string; message: string } | null>(null);
    const PAGE_SIZE = 50;

    const API_BASE = 'https://xijjve6puc6ht4inqmmgbss4ay0earbn.lambda-url.us-east-1.on.aws';

    const loadDomains = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(PAGE_SIZE),
                offset: String(page * PAGE_SIZE),
            });
            if (filter === 'blocked') params.set('blocked', 'true');
            if (filter === 'active') params.set('blocked', 'false');

            const response = await fetch(`${API_BASE}/contacts/domains?${params}`);
            if (response.ok) {
                const data = await response.json();
                setDomains(data.data || []);
                setTotalDomains(data.totalDomains || 0);
            }
        } catch (error) {
            console.error('Error loading domains:', error);
        } finally {
            setIsLoading(false);
        }
    }, [page, filter]);

    useEffect(() => {
        loadDomains();
    }, [loadDomains]);

    const handleBlock = async (domain: string) => {
        if (!window.confirm(`Block "${domain}"? All contacts with this domain will be deactivated and excluded from future sends.`)) return;
        setActionInProgress(domain);
        try {
            const response = await fetch(`${API_BASE}/contacts/domains/${encodeURIComponent(domain)}/deactivate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: 'Blocked from Domains dashboard' })
            });
            if (response.ok) {
                const data = await response.json();
                setNotification({ type: 'success', message: `Blocked ${domain} — ${data.deactivatedCount || 0} contacts deactivated` });
                setDomains(prev => prev.map(d => d.domain === domain ? { ...d, blocked: true, block_reason: 'Blocked from Domains dashboard' } : d));
            } else {
                setNotification({ type: 'error', message: `Failed to block ${domain}` });
            }
        } catch (error) {
            setNotification({ type: 'error', message: `Error blocking ${domain}` });
        } finally {
            setActionInProgress(null);
        }
    };

    const handleUnblock = async (domain: string) => {
        if (!window.confirm(`Unblock "${domain}"? Contacts with this domain can be emailed again.`)) return;
        setActionInProgress(domain);
        try {
            const response = await fetch(`${API_BASE}/contacts/domains/${encodeURIComponent(domain)}/unblock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                setNotification({ type: 'success', message: `Unblocked ${domain}` });
                setDomains(prev => prev.map(d => d.domain === domain ? { ...d, blocked: false, block_reason: null } : d));
            } else {
                setNotification({ type: 'error', message: `Failed to unblock ${domain}` });
            }
        } catch (error) {
            setNotification({ type: 'error', message: `Error unblocking ${domain}` });
        } finally {
            setActionInProgress(null);
        }
    };

    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const filteredDomains = searchQuery.trim()
        ? domains.filter(d => d.domain.toLowerCase().includes(searchQuery.toLowerCase()))
        : domains;

    const totalPages = Math.ceil(totalDomains / PAGE_SIZE);

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb' }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: '28px', color: '#1f2937' }}>Domains</h1>
                    <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>Manage recipient email domains — block or unblock domains from receiving campaigns</p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{ background: 'white', padding: '10px 16px', borderRadius: '8px', border: '2px solid #e5e7eb', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Total Domains</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#3b82f6' }}>{totalDomains.toLocaleString()}</div>
                    </div>
                </div>
            </div>

            {/* Notification */}
            {notification && (
                <div style={{
                    padding: '12px 16px',
                    marginBottom: '16px',
                    borderRadius: '8px',
                    background: notification.type === 'success' ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${notification.type === 'success' ? '#86efac' : '#fca5a5'}`,
                    color: notification.type === 'success' ? '#166534' : '#991b1b',
                    fontWeight: 500,
                    fontSize: '14px'
                }}>
                    {notification.message}
                </div>
            )}

            {/* Controls */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '8px', padding: '4px' }}>
                    {(['all', 'active', 'blocked'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => { setFilter(f); setPage(0); }}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 600,
                                background: filter === f ? '#3b82f6' : 'transparent',
                                color: filter === f ? 'white' : '#6b7280',
                                transition: 'all 0.2s'
                            }}
                        >
                            {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Blocked'}
                        </button>
                    ))}
                </div>
                <input
                    type="text"
                    placeholder="Filter domains..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        padding: '8px 14px',
                        border: '2px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        width: '250px'
                    }}
                />
                <button
                    onClick={loadDomains}
                    disabled={isLoading}
                    style={{
                        padding: '8px 20px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: 600,
                        opacity: isLoading ? 0.6 : 1
                    }}
                >
                    {isLoading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {/* Table */}
            <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                <th style={thStyle}>#</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Domain</th>
                                <th style={thStyle}>Active</th>
                                <th style={thStyle}>Proven</th>
                                <th style={thStyle}>Unproven</th>
                                <th style={thStyle}>Total</th>
                                <th style={thStyle}>Status</th>
                                <th style={thStyle}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && filteredDomains.length === 0 ? (
                                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
                            ) : filteredDomains.length === 0 ? (
                                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No domains found</td></tr>
                            ) : filteredDomains.map((d, idx) => (
                                <tr key={d.domain} style={{ borderBottom: '1px solid #e5e7eb', background: d.blocked ? '#fef2f2' : 'white', transition: 'background 0.2s' }}
                                    onMouseEnter={e => { if (!d.blocked) e.currentTarget.style.background = '#f9fafb'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = d.blocked ? '#fef2f2' : 'white'; }}
                                >
                                    <td style={tdStyle}>{page * PAGE_SIZE + idx + 1}</td>
                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#1f2937' }}>
                                        {d.domain}
                                        {d.block_reason && (
                                            <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 400, marginTop: '2px' }}>{d.block_reason}</div>
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, fontWeight: 700, color: '#3b82f6' }}>{d.active_count.toLocaleString()}</td>
                                    <td style={{ ...tdStyle, color: '#059669' }}>{d.proven_active_count != null ? d.proven_active_count.toLocaleString() : '-'}</td>
                                    <td style={{ ...tdStyle, color: '#d97706' }}>{d.unproven_active_count != null ? d.unproven_active_count.toLocaleString() : '-'}</td>
                                    <td style={tdStyle}>{d.total_count.toLocaleString()}</td>
                                    <td style={tdStyle}>
                                        {d.blocked ? (
                                            <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#fee2e2', color: '#991b1b' }}>Blocked</span>
                                        ) : (
                                            <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: '#dcfce7', color: '#166534' }}>Active</span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        {d.blocked ? (
                                            <button
                                                onClick={() => handleUnblock(d.domain)}
                                                disabled={actionInProgress === d.domain}
                                                style={{
                                                    padding: '4px 12px', background: '#dcfce7', color: '#166534', border: '1px solid #86efac',
                                                    borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, opacity: actionInProgress === d.domain ? 0.5 : 1
                                                }}
                                            >
                                                {actionInProgress === d.domain ? '...' : 'Unblock'}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleBlock(d.domain)}
                                                disabled={actionInProgress === d.domain}
                                                style={{
                                                    padding: '4px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5',
                                                    borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, opacity: actionInProgress === d.domain ? 0.5 : 1
                                                }}
                                            >
                                                {actionInProgress === d.domain ? '...' : 'Block'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                            Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalDomains)} of {totalDomains.toLocaleString()}
                        </span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={page === 0}
                                style={{ ...paginationBtnStyle, opacity: page === 0 ? 0.4 : 1 }}
                            >
                                Previous
                            </button>
                            <span style={{ padding: '6px 12px', fontSize: '13px', color: '#374151', fontWeight: 600 }}>
                                Page {page + 1} / {totalPages}
                            </span>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={page >= totalPages - 1}
                                style={{ ...paginationBtnStyle, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const thStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    textAlign: 'right',
    whiteSpace: 'nowrap'
};

const tdStyle: React.CSSProperties = {
    padding: '10px 14px',
    fontSize: '13px',
    textAlign: 'right',
    color: '#1f2937'
};

const paginationBtnStyle: React.CSSProperties = {
    padding: '6px 14px',
    background: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500
};

export default DomainsDashboard;
